import ManagedSocket from './ManagedSocket'
import { refreshSession, requestWebSocketTicket } from '../../api/api'
import { socketBaseUrl } from '../../utils/socketUrl'

/**
 * A ManagedSocket wired to this app: tickets from /auth/ws-ticket, session
 * renewal through the HTTP client's single refresh, the browser WebSocket,
 * and a ping the consumers answer with pong.
 */
export function createAppSocket({ path, onMessage, onStatus, onFatal, fatalCodes = [], maxAttempts }) {
  return new ManagedSocket({
    wsBase: socketBaseUrl(),
    path,
    getTicket: requestWebSocketTicket,
    // Offline or a 5xx while renewing is not "signed out": let the socket back
    // off and try again. Only a refused refresh token (or none) stops it.
    refreshAuth: async () => {
      const outcome = await refreshSession()
      return outcome?.kind === 'ok' || outcome?.kind === 'unavailable'
    },
    createSocket: url => new WebSocket(url),
    onMessage,
    onStatus,
    onFatal,
    fatalCodes,
    ...(maxAttempts != null ? { maxAttempts } : {}),
    heartbeatPayload: () => JSON.stringify({ type: 'ping' })
  })
}

/**
 * Tie a socket to the page: try at once when the network returns or the tab
 * comes back to the front, instead of waiting out the backoff. With
 * `pauseAfterHiddenMs`, a tab left in the background that long closes its
 * socket (no live updates nobody is looking at) and reopens it on return.
 * Returns an unbind function.
 */
export function bindPageLifecycle(socket, { pauseAfterHiddenMs = null, onResume } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {}
  let pauseTimer = null
  let paused = false

  const resume = () => {
    if (pauseTimer) {
      clearTimeout(pauseTimer)
      pauseTimer = null
    }
    if (paused) {
      paused = false
      socket.start()
    } else {
      socket.kick()
    }
    if (onResume) onResume()
  }

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      resume()
    } else if (pauseAfterHiddenMs != null && !pauseTimer) {
      pauseTimer = setTimeout(() => {
        pauseTimer = null
        if (document.visibilityState !== 'visible' && socket.isRunning) {
          paused = true
          socket.stop()
        }
      }, pauseAfterHiddenMs)
    }
  }
  const onOnline = () => resume()

  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('online', onOnline)
  return () => {
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('online', onOnline)
    if (pauseTimer) clearTimeout(pauseTimer)
  }
}
